import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Paper } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import cx from 'classnames';
import { scWhite } from 'constants/systemColors';
import { compact } from 'lodash/array';
import { forEach } from 'lodash/collection';
import { has } from 'lodash/object';

import LogTableApi from '../../../../../../../../api/patient/LogTableApi';
import { muiDataGridLocaleText } from '../../../../../../../../services/i18n/mui';
import { generateUrlByKey, useCurrentRoute } from '../../../../../../../root/router';
import persistentStorage from '../../persistentStorage';

import ExpandMore from './ExpandMore';
import useDataGrid from './useDataGrid';

import './ItemGroup.scss';

const localeText = muiDataGridLocaleText({ noRowsLabel: 'No source data to display.' });
const pageSizeOptions = [5, 10, 25, 50];
const autosizeOptions = { includeHeaders: true, expand: true };

const ItemGroup = memo(function ItemGroup({ log, updateItemInAllLogs, isActiveElement, lastElementRef }) {
  const { name, status, itemGroupTemplateId } = log;
  const navigate = useNavigate();
  const currentRoute = useCurrentRoute();
  const [expanded, setExpanded] = useState(function() {
    const sectionSessionState = getLogSessionState(itemGroupTemplateId);
    return has(sectionSessionState, 'expanded') ? sectionSessionState.expanded : true;
  });
  const [paginationModel, setPaginationModel] = useState(function() {
    const sectionSessionState = getLogSessionState(itemGroupTemplateId);
    return {
      pageSize: sectionSessionState?.pageSize || 5,
      page: sectionSessionState?.page || 0
    };
  });
  const { timestamp, columns, rows, pinnedColumns, onPinnedColumnsChange, setGridData } = useDataGrid();

  const parentRouteKey = currentRoute.parent?.key;
  const patientId = currentRoute.params.patientId;
  const ssuPatientId = currentRoute.params.ssuPatientId;

  useEffect(() => {
    LogTableApi.getLogTable({ ssuPatient: ssuPatientId, itemGroupTemplateId, hideLoader: true }).then(
      function({ data }) {
        setGridData(collectDataGridData(data));
        updateItemInAllLogs(itemGroupTemplateId, { status: 'LOADED' });
      },
      () => {}
    );
  }, [itemGroupTemplateId, ssuPatientId, updateItemInAllLogs, setGridData]);

  const logPageUrl = useMemo(
    function() {
      return generateUrlByKey(`${parentRouteKey}.Logs`, {
        patientId,
        ssuPatientId,
        patientItemGroupId: itemGroupTemplateId
      });
    },
    [parentRouteKey, patientId, ssuPatientId, itemGroupTemplateId]
  );

  const onRowClick = useCallback(
    function({ id: rowId, row: { itemGroupTemplateVersionId } }) {
      navigate(
        generateUrlByKey(`${parentRouteKey}.Logs.Row`, {
          patientId,
          ssuPatientId,
          patientItemGroupId: itemGroupTemplateId,
          itemGroupTemplateVersionId,
          rowId
        })
      );
    },
    [itemGroupTemplateId, navigate, parentRouteKey, patientId, ssuPatientId]
  );

  return (
    <Paper
      variant="outlined"
      ref={lastElementRef}
      className={cx('val-item-group', {
        'val-item-group-active': isActiveElement
      })}
      id={itemGroupTemplateId}
    >
      <div className="val-item-group-header">
        <Link to={logPageUrl}>{name}</Link>
        <ExpandMore
          expand={expanded}
          onClick={() => {
            setExpanded(expanded => {
              putSectionSessionState(itemGroupTemplateId, { expanded: !expanded });
              return !expanded;
            });
          }}
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </div>
      <Collapse in={expanded} timeout="auto">
        <Box sx={{ width: '100%', minHeight: 160 }}>
          <DataGridPremium
            key={timestamp}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={onPinnedColumnsChange}
            loading={status !== 'LOADED'}
            sx={{
              '--DataGrid-overlayHeight': '50px',
              '.MuiDataGrid-cell--pinnedRight, .MuiDataGrid-cell--pinnedLeft': { backgroundColor: scWhite }
            }}
            autosizeOnMount
            autosizeOptions={autosizeOptions}
            columns={columns}
            getRowId={getRowId}
            rows={rows}
            rowHeight={50}
            disableRowSelectionOnClick
            localeText={localeText}
            disableRowGrouping
            disableAggregation
            disablePivoting
            pagination
            pageSizeOptions={pageSizeOptions}
            paginationModel={paginationModel}
            onPaginationModelChange={paginationModel => {
              putSectionSessionState(itemGroupTemplateId, paginationModel);
              setPaginationModel(paginationModel);
            }}
            onRowClick={onRowClick}
          />
        </Box>
      </Collapse>
    </Paper>
  );
});

export default ItemGroup;

function collectDataGridData({ logData }) {
  const { columnList, tableData } = logData;

  tableData.forEach(function({ data }) {
    forEach(data, function(dataItem) {
      if (dataItem.answer?.includes('$$###$$')) {
        dataItem.answer = compact(dataItem.answer.split('$$###$$')).join(', ');
      }
    });
  });

  return {
    rawColumns: columnList
      .filter(column => !['textBlockLong', 'label'].includes(column.inputType))
      .sort(function(a, b) {
        return a.sequence - b.sequence;
      }),
    rawRows: tableData,
    timestamp: +new Date()
  };
}

function getRowId(row) {
  return row.rowId;
}

function putSectionSessionState(id, changes) {
  if (!id || !changes) {
    return;
  }
  persistentStorage.transact('logs', function(state) {
    if (!state) {
      return { [id]: changes };
    }
    if (!state[id]) {
      state[id] = changes;
    } else {
      state[id] = { ...state[id], ...changes };
    }
    return state;
  });
}

function getLogSessionState(id) {
  return persistentStorage.get('logs')?.[id];
}
